import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createGeneralInquiry } from "../../services/adminService";
import { getCategories } from "../../services/productServices";
import { fetchUser, updateUser } from "../../services/userServices";

export default function Inquiry_form_admin() {
  // --- form fields ---
  const [userName, setUserName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userChatId, setUserChatId] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserChanges, setHasUserChanges] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasSelectedUser, setHasSelectedUser] = useState(false);

  // --- categories ---
  const [categories, setCategories] = useState([]);

  // --- user-lookup states & refs ---
  const [fetchedUser, setFetchedUser] = useState(null);
  const [searchingUser, setSearchingUser] = useState(false);
  const suppressSearchRef = useRef(false);
  const phoneSearchTimeout = useRef(null);

  // --- regex & formatting helpers ---
  const phoneRegex = /^[79][0-9]{8}$/;
  const formatEthiopian = (raw) => {
    const digits = raw.replace(/\D/g, "");
    return `${digits}`;
  };
  const isValidPhoneNumber = (phone) => phoneRegex.test(phone);

  const formatDisplayPhone = (raw) => {
    // for the fetchedUser card
    const digits = raw.replace(/\D/g, "");
    return `${digits}`;
  };

  // --- fetch categories once ---
  useEffect(() => {
    (async () => {
      try {
        const cats = await getCategories();
        setCategories(cats || []);
      } catch (err) {
        console.error("Failed to load categories", err);
        toast.error("Could not load categories");
      }
    })();
  }, []);

  // --- fetch existing user by phone or chatId ---
  const fetchUserByPhoneOrChatId = async (phone, chatId) => {
    try {
      setSearchingUser(true);
      const fullPhone = phone ? `0${phone}` : undefined;
      const response = await fetchUser({
        chat_id: chatId,
        contact_information: fullPhone
      });
      
      if (response.data && response.data.exists && response.data.user) {
        setFetchedUser(response.data.user);
      } else {
        setFetchedUser(null);
        toast.warn("User not found for this phone number or chat ID.", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      toast.error("Could not fetch user info.", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
      });
    } finally {
      setSearchingUser(false);
    }
  };

  useEffect(() => {
    if (suppressSearchRef.current) return;
    clearTimeout(phoneSearchTimeout.current);

    const phoneOK = phoneNumber.length === 9 && isValidPhoneNumber(phoneNumber);
    const chatOK = Boolean(String(userChatId).trim());

    if (phoneOK || chatOK) {
      phoneSearchTimeout.current = setTimeout(() => {
        fetchUserByPhoneOrChatId(
          phoneOK ? phoneNumber : null,
          chatOK ? String(userChatId).trim() : null
        );
      }, 300);
    }
  }, [phoneNumber, userChatId]);

  // --- when admin picks the fetched user ---
  const handleSelectFetchedUser = () => {
    if (!fetchedUser) return;
    suppressSearchRef.current = true;

    // populate fields
    setUserName(fetchedUser.name);
    const raw = (fetchedUser.contact_information || "").trim();
    const normalized = raw.startsWith("") ? raw.slice(1) : raw;
    setPhoneNumber(normalized);
    setUserChatId(String(fetchedUser.chat_id));
    // hide the card and reset changes flag
    setFetchedUser(null);
    setHasUserChanges(false);
    setHasSelectedUser(true);

    // resume auto-search
    setTimeout(() => {
      suppressSearchRef.current = false;
    }, 500);
  };

  const handleUserInfoChange = (field, value) => {
    if (field === 'name') setUserName(value);
    if (field === 'phone') setPhoneNumber(value.replace(/\D/g, ""));
    setHasUserChanges(true);
  };

  const handleUpdateUser = async () => {
    if (!hasUserChanges) return;
    
    setIsUpdating(true);
    try {
      await updateUser({
        chat_id: userChatId,
        name: userName.trim(),
        contact_information: phoneNumber ? `0${phoneNumber}` : null
      });
      toast.success("User updated successfully!");
      setHasUserChanges(false);
    } catch (err) {
      console.error("Error updating user:", err);
      toast.error("Could not update user info.");
    } finally {
      setIsUpdating(false);
    }
  };

  // --- form submission ---
  const handlePost = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!userName.trim()) {
      toast.error("Please enter a name.");
      setIsSubmitting(false);
      return;
    }
    if (!phoneRegex.test(phoneNumber)) {
      toast.error("Phone must start with 7 or 9 and be 9 digits.");
      setIsSubmitting(false);
      return;
    }
    const chatIdStr = String(userChatId).trim();
    if (!chatIdStr) {
      toast.error("Please enter a Chat ID.");
      setIsSubmitting(false);
      return;
    }
    if (!selectedCategoryId) {
      toast.error("Please select a category.");
      setIsSubmitting(false);
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description.");
      setIsSubmitting(false);
      return;
    }

    try {
      await createGeneralInquiry(
        userName.trim(),
        formatEthiopian(phoneNumber),
        description.trim(),
        "website",
        "admin",
        Number(selectedCategoryId),
        chatIdStr
      );
      toast.success("Inquiry created successfully!");
      // reset
      setUserName("");
      setPhoneNumber("");
      setUserChatId("");
      setDescription("");
      setSelectedCategoryId("");
    } catch (err) {
      console.error("Failed to create inquiry", err);
      toast.error("Failed to create inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4">New Inquiry</h2>
      <form onSubmit={handlePost} className="space-y-4">
        {/* Name */}
        <div>
          <label className="block mb-1 font-medium">Name</label>
          <input
            type="text"
            value={userName}
            onChange={(e) => handleUserInfoChange('name', e.target.value)}
            className="w-full border rounded px-3 py-2"
            placeholder="e.g. Desta"
          />
        </div>

        {/* Phone & loading spinner */}
        <div>
          <label className="block mb-1 font-medium">Phone Number</label>
          <div className="relative">
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => 
                handleUserInfoChange('phone', e.target.value)
              }
              maxLength={9}
              className="w-full border rounded px-3 py-2"
              placeholder="912345678"
            />
            {searchingUser && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin border-2 border-t-blue-500 border-gray-300 rounded-full w-5 h-5" />
            )}
          </div>
          <small className="text-gray-500">9 digits, starts with 7 or 9</small>
        </div>

        {/* Chat ID */}
        <div>
          <label className="block mb-1 font-medium">Chat ID</label>
          <input
            type="text"
            value={userChatId}
            onChange={(e) => setUserChatId(e.target.value)}
            className="w-full border rounded px-3 py-2"
            placeholder="e.g. 651560"
          />
        </div>

        {/* Fetched User Card */}
        {fetchedUser && (
          <div className="p-4 bg-gray-100 border rounded">
            <p className="font-medium mb-2">Fetched User:</p>
            <p>
              <strong>Name:</strong> {fetchedUser.name}
            </p>
            {fetchedUser.contact_information && (
              <p>
                <strong>Phone:</strong>{" "}
                {formatDisplayPhone(fetchedUser.contact_information)}
              </p>
            )}
            {fetchedUser.chat_id && (
              <p>
                <strong>Chat ID:</strong> {fetchedUser.chat_id}
              </p>
            )}
            <button
              type="button"
              onClick={handleSelectFetchedUser}
              className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Use this user
            </button>
          </div>
        )}

        {/* Category dropdown */}
        <div>
          <label className="block mb-1 font-medium">Category</label>
          <select
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.topic_id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block mb-1 font-medium">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border rounded px-3 py-2 h-24 resize-none"
            placeholder="Enter inquiry details…"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-2 rounded ${
            isSubmitting ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"
          } text-white font-medium`}
        >
          {isSubmitting ? "Submitting…" : "Submit Inquiry"}
        </button>

        {hasSelectedUser && hasUserChanges && (
          <button
            type="button"
            onClick={handleUpdateUser}
            disabled={isUpdating}
            className={`w-full py-2 rounded ${
              isUpdating ? "bg-gray-400" : "bg-blue-500 hover:bg-blue-600"
            } text-white font-medium mt-2`}
          >
            {isUpdating ? "Updating..." : "Update User Info"}
          </button>
        )}
      </form>

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
}
